<template>
    <div>
        <div class="afu-w-full afu-p-3 sm:afu-p-5 afu-bg-neutral--light afu-shadow-secondary afu-rounded-secondary afu-m-auto sm:afu-max-w-md afu-w-100 afu-box-border">
            <template v-if="state === 'create_password'">
                <div class="afu-text-center afu-mb-11">
                    <h2 class="af-text--9xl afu-text-black afu-font-bold md:afu-font-semibold">
                        {{ $t('create_password.title') }}
                    </h2>

                    <p class="afu-mb-0 afu-text-black">
                        {{ $t('create_password.info') }}
                    </p>
                </div>
            </template>
            <template v-else>
                <div class="afu-text-center afu-mb-11">
                    <h2 class="af-text--9xl afu-text-black">
                        {{ $t('forgot_password.title') }}
                    </h2>

                    <p class="afu-mb-0 afu-text-black">
                        {{ $t('forgot_password.info') }}
                    </p>
                </div>
            </template>

            <C1Alert
                v-if="response.message"
                :variant="response.variant"
            >
                <span v-html="response.message" />
                <a
                    v-if="countDown === 0"
                    href=""
                    class="afu-text-success-500 afu-font-medium afu-underline hover:afu-no-underline"
                    @click.stop.prevent="resendCode()"
                >
                    <BSpinner
                        v-if="spinner.resend_code"
                        small
                    /> {{ $t('forgot_password.resend_code') }}
                </a>
                <span v-else>
                    {{ $t('forgot_password.try_again', {countDown}) }}
                </span>
            </C1Alert>

            <template v-if="state === 'recover'">
                <ValidationObserver
                    ref="forgotPasswordForm"
                    tag="form"
                    class="afu-grid afu-row-gap-4"
                    @submit.stop.prevent="forgotPassword()"
                >
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="email"
                        rules="required|email"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                v-model="email"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="email"
                                :placeholder="$t('label.enter_your_value', {'value': $t('label.placeholder_email')})"
                            />
                        </C1FormGroup>
                    </ValidationProvider>

                    <C1Button
                        type="submit"
                        variant="primary"
                        class="afu-mt-5"
                        block
                        :is-busy="spinner.forgot_password"
                    >
                        {{ $t('label.recover_password') }}
                    </C1Button>
                </ValidationObserver>
            </template>

            <template v-if="state === 'code' || state === 'create_password'">
                <ValidationObserver
                    ref="resetPasswordForm"
                    tag="form"
                    class="afu-grid afu-row-gap-4"
                    @submit.prevent="resetPassword()"
                >
                    <ValidationProvider
                        ref="resetPasswordFormEmail"
                        v-slot="{ errors }"
                        name="Email"
                        rules="required|email"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                v-model="email"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="email"
                                type="email"
                                :placeholder="$t('label.enter_your_value', {'value': $t('label.placeholder_email')})"
                            />
                        </C1FormGroup>
                    </ValidationProvider>

                    <ValidationProvider
                        v-slot="{ errors }"
                        name="Code"
                        rules="required"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                v-model="code"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="code"
                                :placeholder="$t('label.enter_code')"
                            />
                        </C1FormGroup>
                    </ValidationProvider>

                    <ValidationProvider
                        v-slot="{ errors }"
                        name="Password"
                        rules="required|min:6"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                v-model="newPassword"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                type="password"
                                name="newPassword"
                                :placeholder="$t('label.enter_new_password')"
                            />
                        </C1FormGroup>
                    </ValidationProvider>

                    <C1Button
                        type="submit"
                        variant="primary"
                        block
                        :is-busy="spinner.reset_password"
                    >
                        {{ $t('label.sign_in') }}
                    </C1Button>
                </ValidationObserver>
            </template>
        </div>
        <div class="af-login-link afu-text-center afu-mt-5 md:afu-mt-0">
            <a
                :href="getLocalePath('/user-login')"
                class="text-sm af-text--7xl afu-text-primary hover:afu-text-primary afu-underline hover:afu-no-underline afu-font-normal"
            >
                {{ $t('login.back') }}
            </a>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Button from '@/components/C1Components/C1Button';
    import has from 'lodash/has';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import LoginMixin from '@/components/Utils/LoginMixin';

    export default {
        name: 'ForgotPassword',

        components: {
            C1FormGroup,
            ValidationObserver,
            ValidationProvider,
            C1Alert,
            C1FormInput,
            C1Button,
        },

        mixins: [LoginMixin],

        props: {
            prefillEmail: {
                type: String,
                default: '',
            },

            mode: {
                type: String,
                default: 'recover',
            },
        },

        data() {
            return {
                countDown: 0,
                state: this.mode,
                email: this.prefillEmail,
                code: '',
                newPassword: '',
                prevRoute: { name: 'index' },
                response: {
                    status: '',
                    message: '',
                },
                spinner: {
                    reset_password: false,
                    forgot_password: false,
                    resend_code: false,
                },
            };
        },

        computed: {
            ...mapState({
                $_apiMessage: state => state.apiMessage,
            }),
        },

        created() {
            const urlParams = new URLSearchParams(window.location.search);
            this.code = urlParams.get('token');
            this.email = urlParams.get('af_email');

            if (this.code && this.email) {
                this.state = 'code';
            }
        },

        methods: {
            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
                $_resetPassword: 'resetPassword',
            }),

            async resendCode() {
                const validator = await this.$refs.resetPasswordFormEmail.validate();
                if (!validator.valid) {
                    return false;
                }

                if (this.spinner.resend_code) {
                    return false;
                }

                this.spinner.resend_code = true;
                this.$_forgotPassword({ email: this.email, code: true })
                    .then(() => {
                        this.response.variant = 'success';
                        this.response.message = this.$t('forgot_password.success');
                    })
                    .catch(error => {
                        const errorCode =
                            error?.response?.data?.error || 'ServerErrorException';
                        this.response.variant = 'danger';
                        this.response.message = this.$t(`api_message.${errorCode}`);
                    })
                    .finally(() => {
                        this.spinner.resend_code = false;
                        this.countDown = 20;
                        this.countDownTimer();
                    });
            },

            async forgotPassword() {
                const validate = await this.$refs.forgotPasswordForm.validate();
                if (!validate) {
                    return false;
                }

                this.response.variant = '';
                this.response.message = '';

                this.spinner.forgot_password = true;
                this.$_forgotPassword({ email: this.email, code: true })
                    .then(() => {
                        this.state = 'code';

                        this.response.variant = 'success';
                        this.response.message = this.$t(
                            'forgot_password.success_resend_code',
                        );
                    })
                    .catch(error => {
                        const errorCode =
                            error?.response?.data?.error || 'ServerErrorException';
                        this.response.variant = 'danger';
                        this.response.message = this.$t(`api_message.${errorCode}`);
                    })
                    .finally(() => {
                        this.spinner.forgot_password = false;
                        this.countDown = 20;
                        this.countDownTimer();
                    });
            },

            async resetPassword() {
                const validator = await this.$refs.resetPasswordForm.validate();
                if (!validator) {
                    return false;
                }

                this.response.variant = '';
                this.response.message = '';

                this.spinner.reset_password = true;
                this.$_resetPassword({
                    email: this.email,
                    password: this.newPassword,
                    code: this.code,
                })
                    .then(() => {
                        this.login().then(() => {
                            window.location.href = '/user-dashboard';
                        });
                    })
                    .catch(error => {
                        const errorCode = has(error, 'response.data.error')
                            ? error.response.data.error
                            : 'ServerErrorException';
                        this.response.variant = 'danger';
                        this.response.message = this.$t(`api_message.${errorCode}`);
                        this.spinner.reset_password = false;
                    })
                    .finally(() => {});
            },

            login() {
                const data = {
                    email: this.email,
                    password: this.newPassword,
                };

                return this.$_login(data);
            },

            countDownTimer() {
                if (this.countDown > 0) {
                    setTimeout(() => {
                        this.countDown -= 1;
                        this.countDownTimer();
                    }, 1000);
                }
            },
        },
    };
</script>
